import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import history from '@guestyci/history';
import { Spinner, Stack } from '@guestyci/arc';
import { utils } from 'utils';
import { LoginPageShell } from '../layout';
import { HybridOktaLogin } from '../components';
import { useSyncSessionStorage } from '../hooks';
import {
  getScriptUrl,
  createSecurityScript,
  isScriptAlreadyLoaded,
  isLiteActivation,
  getOnboardingStep,
  redirectIfActiveSession,
} from '../utils';
import { clearDataDogUser, setSecurityScriptDDContext } from '../../login/helpers';
import { reportActionToDataDog, reportErrorToDataDog } from '../../../utils/datadog';
import LinkExpiredPage from './LinkExpiredPage';
import { getLanguage } from '../utils/localeHelper';

const LoginPage = () => {
  const {
    return_to: zendeskRedirectUrl,
    ref: urlRef,
    activation: isActivationFlow,
    package: packageName,
    isOnboarding: isOnboardingFlow,
    mailRef: userEmail,
    token: recoveryToken,
  } = history.getQueryParams();

  const [isTokenExpired, setIsTokenExpired] = useState(recoveryToken ? null : false);
  const [isWelcomeStep, setIsWelcomeStep] = useState(false);

  const { oktaAuth } = useOktaAuth();
  const location = useLocation();

  useSyncSessionStorage('zendeskRedirectUrl', zendeskRedirectUrl);
  useSyncSessionStorage('ref', urlRef);
  useSyncSessionStorage('isActivationFlow', isActivationFlow);
  useSyncSessionStorage('isOnboardingFlow', isOnboardingFlow === 'true');

  const [, setCurrentLanguage] = useState(getLanguage());
  const handleLanguageChange = useCallback(() => setCurrentLanguage(getLanguage()), []);

  const [oktaViewState, setOktaViewState] = useState(null);
  const [registrationStep, setRegistrationStep] = useState(null);
  const showStepper = sessionStorage.getItem('isOnboardingFlow');
  const currentStep = getOnboardingStep(oktaViewState, registrationStep);

  const handleViewStateChange = useCallback((viewState, regStep) => {
    setOktaViewState(viewState);
    setRegistrationStep(regStep);
  }, []);

  useEffect(() => {
    redirectIfActiveSession(oktaAuth, location.state);
  }, [
    oktaAuth,
    location.state,
  ]);

  useEffect(() => {
    if (userEmail && typeof userEmail === 'string' && userEmail.endsWith('@guesty.com')) {
      window.localStorage.setItem('shouldSkipCreditCardStep', 'true');
    } else {
      window.localStorage.removeItem('shouldSkipCreditCardStep');
    }
  }, [userEmail]);

  // Security script injection
  const injectScript = useCallback(() => {
    const scriptSrc = getScriptUrl();

    if (isScriptAlreadyLoaded(scriptSrc)) {
      return;
    }

    const onSuccess = () => setSecurityScriptDDContext(true, scriptSrc);
    const onError = () => {
      const errorMsg = 'Security script failed to load';
      console.error(errorMsg);
      reportErrorToDataDog(errorMsg, {
        errorLocation: 'LoginPage.injectScript',
        scriptSrc,
      });
      setSecurityScriptDDContext(false, scriptSrc);
    };

    const script = createSecurityScript(scriptSrc, onSuccess, onError);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const handle = window.requestIdleCallback(injectScript);
      return () => window.cancelIdleCallback(handle);
    }

    window.addEventListener('load', injectScript);
    return () => window.removeEventListener('load', injectScript);
  }, [injectScript]);

  useEffect(() => {
    if (!isLiteActivation(isActivationFlow, packageName)) {
      return;
    }

    clearDataDogUser();
    reportActionToDataDog('User email verified');
  }, [isActivationFlow, packageName]);

  utils.useDocumentTitle('Login');
  utils.usePageView('login');

  const isLoadingTokenValidation = isTokenExpired === null;

  if (isTokenExpired) {
    return <LinkExpiredPage />;
  }

  return (
    <>
      {isLoadingTokenValidation && (
        <Stack
          align="center"
          justify="center"
          className="gst-fixed gst-inset-0 gst-z-50 gst-bg-white"
        >
          <Spinner size="xl" />
        </Stack>
      )}
      <LoginPageShell isWelcomeStep={isWelcomeStep} onLanguageChange={handleLanguageChange} showStepper={showStepper && currentStep !== null} currentStep={currentStep}>
        <HybridOktaLogin
          onViewStateChange={handleViewStateChange}
          onTokenExpired={setIsTokenExpired}
          onWelcomeStepChange={setIsWelcomeStep}
        />
      </LoginPageShell>
    </>
  );
};

export default LoginPage;
